import { usePagination, useUpdateQueryParams } from 'vopay';
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ACCOUNT_TYPE_BY_FILTER,
  AccountRow,
  AccountSearchParams,
  toAccountTypeFilter,
  toCurrencyFilter,
} from './types';
import { getAccountRowToken } from './buildAccountRows';
import { getAccountHolderLabel } from './accountLabels';

// The state half of an account listing (Account & Billing's payment methods and the client portal's
// Operational Accounts): the filters in the URL, the keyword/type match, the page, and which row the
// details drawer is showing. AccountListingPanel renders from the result.
//
// Deliberately NOT here: building the rows, and the fetch behind the Search button. Row production
// differs by listing — each resolves its default flags off a different set of defaults — and the two
// pages call different endpoints with different params, so both stay with the caller and arrive as
// `rows` and `search`.

const PER_PAGE = 20;

export type UseAccountListingParams = {
  /** The listing's own rows, unfiltered and unsorted — the caller builds them, the hook filters and pages them. */
  rows: AccountRow[];
  /**
   * Where the details drawer looks its row up: every row on the account, not just this listing's.
   * The drawer opens for accounts the table isn't showing (Operational's default-funding cards), and
   * for rows on other pages of the listing.
   */
  detailRows: AccountRow[];
  /** Refetches the listing for the given filters. Each page calls its own endpoint, so it stays with the page. */
  search: (searchParams: AccountSearchParams) => void;
};

export type AccountListing = {
  /** The filters currently in the URL — the form renders from these, and the page's own fetches read the currency. */
  queryParams: AccountSearchParams;
  filtersApplied: boolean;
  /** Applies filters: resets to the first page, writes them to the URL, and runs the caller's `search`. */
  submitSearch: (searchParams: AccountSearchParams) => void;
  /** Exactly the rows the table should render, in order. */
  pagedAccounts: AccountRow[];
  pageCount: number;
  pageIndex: number;
  setPageIndex: (pageIndex: number) => void;
  /** The row the details drawer is showing, or null when it is closed. */
  selectedAccount: AccountRow | null;
  openAccountDetails: (token?: string) => void;
  closeAccountDetails: () => void;
};

export default function useAccountListing({ rows, detailRows, search }: UseAccountListingParams): AccountListing {
  const location = useLocation();
  const updateQueryParams = useUpdateQueryParams<AccountSearchParams>();
  const [selectedAccountToken, setSelectedAccountToken] = useState<string | null>(null);

  const queryParams: AccountSearchParams = useMemo(() => {
    const params = new URLSearchParams(location.search);

    // Narrowed rather than trusted: the values are arbitrary URL text, so a hand-edited
    // `?type=banana` reads as "no filter" instead of typing as one.
    return {
      type: toAccountTypeFilter(params.get('type')),
      currency: toCurrencyFilter(params.get('currency')),
      searchTerm: params.get('searchTerm') || '',
    };
  }, [location.search]);

  const { type, searchTerm } = queryParams;

  const filtersApplied = useMemo(() => Object.values(queryParams).some((val) => val !== ''), [queryParams]);

  // Currency is a request parameter, not a row match — the backend already returned only that
  // currency — so only type and keyword are applied here.
  const filteredRows = useMemo(() => {
    const searchNeedle = searchTerm.toLowerCase();

    // Only these row fields carry a human name: rows are built by spreading ClientBankAccountsLists /
    // ClientCreditCardLists, and neither has any other name field.
    return rows.filter(
      (row) =>
        // The filter's URL values are compact ('BankAccount') and the row's are not ('Bank Account'),
        // so one typed map ties them together — stripping the whitespace to compare leaves nothing
        // checking that the two vocabularies still agree.
        (type === '' || type === 'All' || row.Type === ACCOUNT_TYPE_BY_FILTER[type]) &&
        (searchNeedle === '' || getAccountHolderLabel(row).toLowerCase().includes(searchNeedle)),
    );
  }, [rows, type, searchTerm]);

  const { pageCount, pageIndex, offset, setPageIndex } = usePagination(filteredRows.length, { pageSize: PER_PAGE });

  // The table renders exactly the rows it is handed, so defaults-first ordering and the page slice
  // happen here, next to the pagination state. Sort a copy — the in-place sort must not reach the
  // rows the caller handed over.
  const pagedAccounts = useMemo(
    () =>
      [...filteredRows]
        .sort((a, b) => (a.IsDefault === b.IsDefault ? 0 : a.IsDefault ? -1 : 1))
        .slice(offset, offset + PER_PAGE),
    [filteredRows, offset],
  );

  // The drawer holds a token rather than a row so it never shows a stale copy: the row it renders is
  // looked up in the current list, and a refresh that changes or removes the account is reflected
  // without anything having to reconcile it.
  const selectedAccount = useMemo(() => {
    if (selectedAccountToken === null) return null;

    return detailRows.find((row) => getAccountRowToken(row) === selectedAccountToken) ?? null;
  }, [detailRows, selectedAccountToken]);

  return {
    queryParams,
    filtersApplied,
    submitSearch: (searchParams: AccountSearchParams) => {
      setPageIndex(0);
      updateQueryParams(searchParams);
      search(searchParams);
    },
    pagedAccounts,
    pageCount,
    pageIndex,
    setPageIndex,
    selectedAccount,
    openAccountDetails: (token?: string) => setSelectedAccountToken(token || null),
    closeAccountDetails: () => setSelectedAccountToken(null),
  };
}
